import { CURRENCY_SYMBOLS } from '@ddt-web/common';
import { NumberWithUnitsFormatter } from '@ddt-web/data-service';
import { ObjectUtil } from '@ddt-web/generic-utils';
import * as Highcharts from 'highcharts';
import more from 'highcharts/highcharts-more';
import * as get from 'lodash/get';

import { ChartFactory } from '../factories';
import { TooltipDispatcher } from '../tooltips/dispatcher.tooltip';
import { ChartConfigHelper } from './config.helper';
import { DefaultChartConfig } from './default-chart.config';
import { ChartType } from './enums/chart-type.enum';

more(Highcharts);

const DEFAULT_CONFIG: any = {
	legend: {
		enabled: true,
		align: 'bottom',
		layout: 'vertical',
		verticalAlign: 'bottom',
		itemMarginBottom: 10
	}
};

export class AreaSplineChartConfig extends DefaultChartConfig {
	private seriesColors: Array<string> = [];

	private multiplier: number = 10000;
	private displayCurrency: string = CURRENCY_SYMBOLS.USD;

	constructor(
		protected type: ChartType,
		protected name: string,
		protected container: HTMLElement,
		protected data,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: any,
		protected tooltipType?: string
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	public render(): void {
		Highcharts.chart(this.configuration);
	}

	public getConfigurationForPDF(): void {
		return this.configuration;
	}

	private initialize(): void {
		this.displayCurrency = this.customAttributes?.displayCurrency;
		this.configuration = ObjectUtil.extend(this.configuration, DEFAULT_CONFIG);
		this.multiplier = this.customAttributes?.growthMultiplier || 10000;
		this.seriesColors = this.configuration.colors || ChartFactory.getPortfolioColors();
		this.updateDefaultConfiguration();
		const { growthMultiplier, displayCurrency, tooltipPrefixes, multiSeriesConfig, ...validHighChartAttributes } = this.customAttributes;
		this.configuration = ChartConfigHelper.applyCustomAttributesToConfiguration(validHighChartAttributes, this.configuration);
	}

	private updateDefaultConfiguration(): void {
		const currency = this.displayCurrency;
		const formatter = new NumberWithUnitsFormatter('0', '-', true);
		const isPdf = this.customAttributes?.isPdf;
		if (this.customAttributes?.linearChart) {
			const customAttributes = this.customAttributes;
			this.configuration = ObjectUtil.extend(this.configuration, {
				colors: this.seriesColors,
				series: get(this.data[0], 'data', []),
				chart: {
					marginTop: 10,
					marginBottom: 48
				},
				legend: {
					enabled: false
				},
				tooltip: {
					shared: true,
					backgroundColor: 'rgba(255, 255, 255, 0.9)',
					borderWidth: 1,
					useHTML: true,
					formatter: new TooltipDispatcher(this.type, this.tooltipType).getTooltipFormatter({
						multiplier: this.multiplier,
						displayCurrency: this.displayCurrency,
						customData: get(this.data[0], 'tooltipData', {})
					})
				},
				xAxis: {
					changed: false,
					tickInterval: 1,
					title: {
						text: this.customAttributes?.xAxisTitle,
						style: { fontSize: isPdf ? '8px' : '14px' }
					},
					labels: {
						style: { fontSize: isPdf ? '8px' : '12px' }
					}
				},
				yAxis: {
					startOnTick: false,
					labels: {
						formatter: function (): string {
							return currency + formatter.format(Number(this.value));
						},
						align: 'right',
						style: {
							color: 'grey',
							fontSize: isPdf ? '8px' : '12px'
						}
					},
					opposite: false,
					title: {
						text: this.customAttributes?.yAxisTitle,
						style: { fontSize: isPdf ? '8px' : '14px' }
					}
				},
				plotOptions: {
					areaspline: {
						marker: {
							enabled: false
						},
						stacking: 'normal'
					},
					series: {
						lineWidth: 3,
						point: {
							events: {
								click: function (): void {
									if (this.category === 85) {
										customAttributes?.callbackFunctions?.toggleMessageForAgeEqual85();
									} else {
										customAttributes?.callbackFunctions?.toggleMessageForAgeOtherThan85('line');
									}
								}
							}
						}
					}
				}
			});
		} else {
			this.configuration = ObjectUtil.extend(this.configuration, {
				colors: this.seriesColors,
				series: get(this.data[0], 'data', []),
				chart: {
					marginTop: 10,
					marginBottom: 60 + 24 * this.data.length
				},
				legend: {
					enabled: false
				},
				tooltip: {
					shared: true,
					backgroundColor: 'rgba(255, 255, 255, 0.9)',
					borderWidth: 1,
					useHTML: true,
					formatter: new TooltipDispatcher(this.type, this.tooltipType).getTooltipFormatter({
						multiplier: this.multiplier,
						displayCurrency: this.displayCurrency,
						customData: this.customAttributes.customData
					})
				},
				xAxis: {
					changed: false,
					type: 'datetime',
					labels: {
						formatter: function (): string {
							return Highcharts.dateFormat('%m/%d/%Y', this.value);
						},
						style: {
							color: 'grey'
						}
					},
					tickPositions: this.updateTickPositions()
				},
				yAxis: {
					min: this.getMinimumYAxisValue(),
					startOnTick: false,
					labels: {
						formatter: function (): string {
							return currency + Highcharts.numberFormat(Math.abs(this.value), 0, '.', ',');
						},
						align: 'right',
						style: {
							color: 'grey'
						}
					},
					opposite: false,
					title: {
						enabled: false
					}
				},
				plotOptions: {
					areaspline: {
						marker: {
							enabled: false
						},

						stacking: 'normal'
					},
					series: {
						lineWidth: 3
					}
				}
			});
		}
	}

	private updateTickPositions(): Array<Date> {
		const ticks = [];
		const data = get(this.data[0], 'data', []);
		const dates = data?.[1]?.data || [];
		let step: number;
		if (dates.length > 6) {
			step = 5;
		} else {
			step = 1;
		}
		for (let i = 0; i < dates.length; i += step) {
			ticks.push(dates[i][0]);
		}
		return ticks;
	}

	private getMinimumYAxisValue(): number {
		return this.data?.[0]?.basePortfolioValue || 0;
	}
}
